import colors from '@/colors';
import Logotype from '@/components/elements/Logotype';
import LoginModal from '@/components/modules/modals/LoginModal';
import Modal from '@/components/modules/modals/redesign/Modal/Modal';
import { DEFAULT_NAVBAR_HEIGHT, Z_INDEX } from '@/constants/styleConstants';
import { classnames } from '@/helpers';
import { useAppSelector } from '@/hooks';
import { LoginContext } from '@/hooks/useLogin';
import useMobileView from '@/hooks/useMobileView';
import { _s, getLang } from '@/locale';
import { Fragment, MouseEvent, useContext, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import AccountNavigation from '../account-navigation/AccountNavigation';
import { AccountNavigationContext } from '../account-navigation/AccountNavigation.hooks';
import { MegaMenuContext } from '../mega-menu/MegaMenu.hooks';
import Button from './components/Button';
import { getSearchHeaderButtons } from './components/Button.helpers';
import { ButtonIdentifier, Button as ButtonType } from './components/Button.types';
import AccountNavigationPopover from './modules/AccountNavigationPopover';
import LoginPopover from './modules/LoginPopover';
import useSearchHeader from './SearchHeader.hooks';
import { SearchHeaderProps } from './SearchHeader.types';

const LOGIN_POPOVER_CONTAINER_ID = 'login-popover-container';
const ACCOUNT_NAVIGATION_POPOVER_CONTAINER_ID = 'account-navigation-popover-container';

const baseTranslationKey = 'features.search-header.SearchHeader';

export const SEARCH_HEADER_MAX_HEIGHT = {
  DESKTOP: 128,
  MOBILE: 120,
};

export const SEARCH_HEADER_CONTENT_HEIGHT = {
  DESKTOP: 72,
  MOBILE: 64,
} as const;

const SearchHeaderContent = ({
  back,
  onBackButtonClick,
  backSrc,
  backIcon,
  leftSlot,
  middleSlot,
  searchHeaderButtons,
}: SearchHeaderProps) => {
  const { isMobileView } = useMobileView();

  const accountNavigationContext = useContext(AccountNavigationContext);
  const loginContext = useContext(LoginContext);
  const megaMenuContext = useContext(MegaMenuContext);
  const height = SEARCH_HEADER_CONTENT_HEIGHT[isMobileView ? 'MOBILE' : 'DESKTOP'];

  const handleNavItemClick = (event: MouseEvent<HTMLElement>, item: ButtonType) => {
    event.currentTarget?.blur?.();

    if (item.identifier === ButtonIdentifier.LogIn) {
      loginContext.show ? loginContext.onClose() : loginContext.onOpen();
      return;
    }

    if (item.identifier === ButtonIdentifier.Profile) {
      accountNavigationContext.show ? accountNavigationContext.onClose() : accountNavigationContext.onOpen();
      return;
    }
  };

  return (
    <div
      className="gap-md container-layout bg-surface_l2 flex items-center justify-between"
      style={{ height }}
      {...(megaMenuContext.activeTab && {
        onClick: () => megaMenuContext.onNavigationLeave(),
      })}>
      <div className="gap-sm flex flex-shrink-0 items-center">
        {back && (
          <Button
            isActive={false}
            {...(onBackButtonClick && { onClick: onBackButtonClick })}
            item={{ to: backSrc ?? '', identifier: ButtonIdentifier.Back, icon: backIcon ?? 'back' }}
            aria-label={_s('back')}
          />
        )}
        {Boolean(leftSlot) && leftSlot}
        {leftSlot === undefined && (
          <Link to="/" aria-label="Bokadirekt">
            <Logotype className="not-sr-only block xl:hidden" variant="symbol" />
            <Logotype className="not-sr-only hidden xl:block" variant="logo" />
          </Link>
        )}
      </div>
      {middleSlot && <div className="mx-auto w-full max-w-3xl">{middleSlot}</div>}
      <Tooltip id="button-tooltip" delayShow={500} style={{ zIndex: 100, background: colors.black['900'] }} />

      {Boolean(searchHeaderButtons.length) && (
        <div
          className="gap-xs flex flex-shrink-0 items-center"
          aria-label={_s(`${baseTranslationKey}.user-navigation-items`)}
          role="navigation">
          {searchHeaderButtons.map((item) => {
            let controls: string = undefined;
            const isActive = item.identifier === ButtonIdentifier.LogIn && loginContext.show;
            const ariaExpanded =
              item.identifier === ButtonIdentifier.Profile
                ? accountNavigationContext.show
                : item.identifier === ButtonIdentifier.LogIn
                ? loginContext.show
                : undefined;
            const ariaHasPopup =
              item.identifier === ButtonIdentifier.Profile || item.identifier === ButtonIdentifier.LogIn
                ? 'true'
                : undefined;

            switch (item.identifier) {
              case ButtonIdentifier.Profile:
                controls = ACCOUNT_NAVIGATION_POPOVER_CONTAINER_ID;
                break;
              case ButtonIdentifier.LogIn:
                controls = LOGIN_POPOVER_CONTAINER_ID;
                break;
              default:
                controls = undefined;
                break;
            }

            let tooltipContent: string = undefined;

            switch (item.identifier) {
              case ButtonIdentifier.Bookings:
                tooltipContent = getLang() === 'sv' ? 'Bokningar' : 'My bookings';
                break;
              case ButtonIdentifier.Favorites:
                tooltipContent = getLang() === 'sv' ? 'Favoriter' : 'Favorites';
                break;
              default:
                tooltipContent = undefined;
                break;
            }

            return (
              <Button
                {...(tooltipContent && {
                  'data-tooltip-id': 'button-tooltip',
                  'data-tooltip-content': tooltipContent,
                })}
                isActive={isActive}
                key={item.identifier}
                onClick={(event) => handleNavItemClick(event, item)}
                item={{ ...item, label: !isMobileView ? item.label : null }}
                /**
                 * Accessibilty proos
                 */
                {...(!isMobileView && {
                  controls,
                  'aria-expanded': ariaExpanded,
                  'aria-haspopup': ariaHasPopup,
                })}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

const SearchHeader = ({
  floatingSlot,
  prepend,
  searchHeaderButtons,
  defaultNavBarHeight = DEFAULT_NAVBAR_HEIGHT,
  bottomSlot,
  ...props
}: SearchHeaderProps) => {
  const { isMobileView } = useMobileView();
  const accountNavigationContext = useContext(AccountNavigationContext);
  const loginContext = useContext(LoginContext);
  const { headerHeight, headerRef } = useSearchHeader();
  const user = useAppSelector((state) => state.users?.user);
  const history = useHistory();
  const defaultGoBack = () => history.go(-1);

  return (
    <header>
      <div
        className={classnames('shadow-xs bg-surface_l2 !fixed top-0 w-full')}
        ref={headerRef}
        style={{ zIndex: Z_INDEX.NAVBAR }}>
        {prepend}
        <div className="container-layout px-lg relative">
          <SearchHeaderContent
            {...props}
            onBackButtonClick={props.onBackButtonClick ?? defaultGoBack}
            searchHeaderButtons={searchHeaderButtons ?? getSearchHeaderButtons(user, isMobileView)}
          />
          {!isMobileView && (
            <Fragment>
              {accountNavigationContext.show && (
                <div className="fixed inset-0 z-10" onClick={accountNavigationContext.onClose} />
              )}
              <div
                className="relative z-10 w-full"
                id={ACCOUNT_NAVIGATION_POPOVER_CONTAINER_ID}
                role="menu"
                {...(!accountNavigationContext.show && { hidden: true })}>
                {accountNavigationContext.show && (
                  <div className="absolute right-0 top-0">
                    <AccountNavigationPopover {...accountNavigationContext} />
                  </div>
                )}
              </div>
              {loginContext.show && <div className="fixed inset-0 z-10" onClick={loginContext.onClose} />}
              <div
                className="relative z-10 w-full"
                id={LOGIN_POPOVER_CONTAINER_ID}
                role="menu"
                {...(!loginContext.show && { hidden: true })}>
                {loginContext.show && (
                  <div className="absolute right-0 top-0">
                    <LoginPopover />
                  </div>
                )}
              </div>
            </Fragment>
          )}
          {bottomSlot}
        </div>
      </div>
      {floatingSlot && (
        <div className="fixed top-0 z-[20] w-full duration-300" style={{ top: headerHeight ?? defaultNavBarHeight }}>
          {floatingSlot}
        </div>
      )}
      {/* Spacer */}
      <div className="bg-transparent" style={{ height: headerHeight ?? defaultNavBarHeight }}></div>

      {isMobileView && (
        <Fragment>
          {accountNavigationContext.show && (
            <Modal isOpen shouldCloseOnOverlayClick={true}>
              <div className="fixed inset-0" onClick={accountNavigationContext.onClose} />
              <Modal.Content floating>
                <AccountNavigation {...accountNavigationContext} />
              </Modal.Content>
            </Modal>
          )}
          {loginContext.show && <LoginModal isOpen source="top_navbar" />}
        </Fragment>
      )}
    </header>
  );
};

export const HideOnScrollWrapper = ({
  children,
  height = 0,
  onScrollChangeCallback,
}: {
  children?: React.ReactNode;
  height: number;
  onScrollChangeCallback?: (visible: boolean) => void;
}) => {
  const [show, setShow] = useState(true);

  const handleScrollUpdate = (visible: boolean) => {
    setShow(visible);
    onScrollChangeCallback?.(visible);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > height) {
        handleScrollUpdate(false);
      } else {
        handleScrollUpdate(true);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative">
      <div className="w-full duration-200" style={{ height: show ? height : 0 }}></div>
      <div
        className={classnames(
          'bg-surface-l2 absolute inset-0 duration-200',
          !show && 'pointer-events-auto -z-[1000] h-0 opacity-0',
        )}
        style={{ transform: `translateY(${show ? 0 : -height}px)` }}>
        {children}
      </div>
      {/* Spacer */}
    </div>
  );
};

export default SearchHeader;
